import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import eq from 'editor/helpers/eq';
import gt from 'editor/helpers/gt';
import notEq from 'editor/helpers/not-eq';
import round from 'editor/helpers/round';
import Command, { CompositeCommand } from 'editor/models/command';
import Drumset, { Velocity, type Instrument } from 'editor/models/drumset';
import { DuplicateDrumsetCommand, DeleteDrumsetCommand } from 'editor/models/drumset-commands';
import {
  AddSamplesToInstrumentCommand,
  RemoveSampleFromInstrumentCommand,
  UpdateSampleVelocityCommand,
  RenameVelocityCommand,
} from 'editor/models/drumset-commands';
import disabled from 'editor/modifiers/disabled';
import markDroppable from 'editor/modifiers/mark-droppable';
import onKey from 'editor/modifiers/on-key';
import scrollIntoView from 'editor/modifiers/scroll-into-view';
import selected from 'editor/modifiers/selected';
import sortable from 'editor/modifiers/sortable';
import type SongPlayerService from 'editor/services/song-player';
import type UndoManagerService from 'editor/services/undo-manager';
import bbffFilename from 'editor/utils/bbff-filename';
import constrainValue from 'editor/utils/constrain-value';
import { getGainNodeForVelocity } from 'editor/utils/play-instrument';
import { modifier } from 'ember-modifier';
import Button from './button';
import SelectableItem from './selectable-item';
import nameFilter from 'editor/utils/name-filter';
import type ProjectManagerService from 'editor/services/project-manager';
import Swal from 'sweetalert2';
import wait from 'editor/utils/wait';



class VelocityGroup {
  vel: number = 0;
  velocities: Velocity[] = [];

  constructor(velocity: Velocity) {
    this.vel = velocity.vel;
    this.velocities.push(velocity);
  }
}

function autoSort(samples: Velocity[]): [Velocity, number][] {
  if (samples.length === 0) return [];

  const sorted = samples.toSorted((v1, v2) => v1.loudness - v2.loudness);
  if (sorted.length < 2) {
    return samples.map((v) => [v, v.vel ?? 0]);
  }
  // Take the sorted array and group it into sub arrays where the difference between the loudness of two consecutive elements is less than 1 dB
  const groups: Velocity[][] = [];
  let currentGroup: Velocity[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const velocity = sorted[i]!;
    const nextVelocity = sorted[i + 1];
    currentGroup.push(velocity);
    if (nextVelocity && nextVelocity.loudness - currentGroup[0]!.loudness > 1.5) {
      groups.push(currentGroup);
      currentGroup = [];
    }
  }
  if (currentGroup.length) {
    groups.push(currentGroup);
  }
  const loudest = 0;
  const quietest = sorted[0]!.loudness;
  const velocities: [Velocity, number][] = [];
  const firstGroup = groups[0]!;
  firstGroup.forEach((v) => {
    velocities.push([v, 0]);
  });
  for (var i = 1, ii = groups.length; i < ii; i++) {
    const group = groups[i]!;
    const avg = group.reduce((acc, v) => acc + v.loudness, 0) / group.length;
    // Calculate where avg sit between the quietest and the loudest?
    const position = (avg - quietest) / (loudest - quietest);

    const minVelocity = 8; // minimum velocity value
    const maxVelocity = 111; // maximum velocity value

    const velocity = Math.round(minVelocity + position * (maxVelocity - minVelocity));

    group.forEach((v) => {
      velocities.push([v, velocity]);
    });
  }
  return velocities;
}

const changeVelocity = modifier(
  (
    element: HTMLElement,
    [group, minVelocity, maxVelocity]: [VelocityGroup, number, number],
  ) => {
    let parentElement: HTMLElement,
      dragOffsetY = 0;

    const moveTo = (y: number) => {
      element.style.transform = `translate3d(0, ${y}px, 0)`;
    };

    const onMouseMove = (ev: MouseEvent) => {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      const rect = parentElement.getBoundingClientRect();
      const maxY = rect.bottom - (maxVelocity / 127) * rect.height;
      const minY = rect.bottom - (minVelocity / 127) * rect.height;
      let y = constrainValue(ev.clientY, maxY, minY);
      const posVel = Math.round((1 - (y - rect.top) / rect.height) * 127);
      const velocity = constrainValue(posVel, minVelocity, maxVelocity);

      document
        .querySelectorAll('input.vel-group')
        .forEach((el) =>
          el.dispatchEvent(
            new CustomEvent('bbff.group-vel-change', { detail: { group, velocity } }),
          ),
        );

      moveTo(y - dragOffsetY);
    };

    const onPointerUp = (ev: PointerEvent) => {
      document.removeEventListener('pointerup', onPointerUp);
      element.releasePointerCapture(ev.pointerId);
    };

    const onMouseUp = (ev: MouseEvent) => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);

      const rect = parentElement.getBoundingClientRect();
      let y = constrainValue(ev.clientY, rect.top, rect.bottom);
      const posVel = Math.round((1 - (y - rect.top) / rect.height) * 127);
      const velocity = constrainValue(posVel, minVelocity, maxVelocity);

      parentElement.dispatchEvent(
        new CustomEvent('bbff.group-vel-change', { detail: { group, velocity } }),
      );
      dragOffsetY = 0;
    };

    const onPointerDown = (ev: PointerEvent) => {
      document.addEventListener('pointerup', onPointerUp);
      element.setPointerCapture(ev.pointerId);
    };

    const onMouseDown = (ev: MouseEvent) => {
      ev.preventDefault();

      dragOffsetY = ev.clientY;

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const cancelDragNDrop = (e: DragEvent) => {
      e.preventDefault();
    };

    parentElement = element.parentElement!;

    element.addEventListener('mousedown', onMouseDown, { capture: true });
    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('dragstart', cancelDragNDrop);

    return () => {
      element.removeEventListener('dragstart', cancelDragNDrop);
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('mousedown', onMouseDown);
    };
  },
);

interface SampleEditorSignature {
  Element: HTMLDivElement;
  Args: {
    drumset: Drumset;
    instrument: Instrument;
  };
  Blocks: {
    default: [addSamples: () => void, autoSort: () => void];
  };
}

export default class SampleEditor extends Component<SampleEditorSignature> {
  @service declare projectManager: ProjectManagerService;
  @service declare songPlayer: SongPlayerService;
  @service declare undoManager: UndoManagerService;
  @service declare router: RouterService;

  undoUpdateTimer: number = 0;

  constructor(owner: unknown, args: SampleEditorSignature['Args']) {
    super(owner, args);
  }

  get audioContext() {
    return this.songPlayer.audioContext;
  }

  get drumset() {
    return this.args.drumset;
  }

  get instrument() {
    return this.args.instrument;
  }

  get velocityGroups() {
    const groups: VelocityGroup[] = [];
    this.instrument.velocities.forEach((velocity) => {
      const group = groups.find((g) => g.vel === velocity.vel);
      if (group) {
        group.velocities.push(velocity);
      } else {
        groups.push(new VelocityGroup(velocity));
      }
    });
    return groups.reverse();
  }

  @tracked isDragging = false;
  dragTarget?: EventTarget | null;

  @action
  handleDragEnter(ev: DragEvent) {
    this._cancelEvents(ev);
    this.isDragging = true;
    this.dragTarget = ev.target;
  }

  @action
  handleDragOver(ev: DragEvent) {
    this._cancelEvents(ev);
    if (ev.dataTransfer) {
      ev.dataTransfer.dropEffect = 'copy';
    }
  }

  @action
  handleDragLeave(ev: DragEvent) {
    if (ev.target == this.dragTarget) {
      this._cancelEvents(ev);
      this.isDragging = false;
      this.dragTarget = undefined;
    }
  }

  @action
  async handleDrop(group: VelocityGroup, ev: DragEvent) {
    let files = ev.dataTransfer?.files;
    this.isDragging = false;
    this.dragTarget = undefined;
    if (files?.length) {
      this._cancelEvents(ev);
      this.#addSampleFiles(files, group);
    }
  }


  @action
  deleteDrumset(drumset: DrumsetFile) {
    const command = new DeleteDrumsetCommand(this.projectManager, drumset);
    command.on('execute', () => {
      this.router.transitionTo('restricted.drumsets.index');
    });
    command.on('reverse', (drumset) => {
      this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
    });
    this.undoManager.executeCommand(command);
  }

  @action
  async convertDrumset() {
    const drumset = this.projectManager.currentDrumset;
    await wait(async () => {
      try {
        const command = new DuplicateDrumsetCommand(this.projectManager, drumset, true);
        command.on('execute', (drumset) => {
          this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
        });
        command.on('reverse', (drumset) => {
          this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
        });
        try {
          await this.undoManager.executeCommand(command);
          //BB2 requires version 2, revision 0
          command.newDrumset.drumset.header.version = 2;
          command.newDrumset.drumset.header.revision = 0;
        } catch (err: any) {
          alert(`Error duplicating drumset`);
          throw err;
        }
      } catch (err: any) {
        alert(`Error converting drumset`);
        throw err;
      }
    });
  }

  @action
  async saveAndConvert(replace: boolean) {
    const drumset = this.projectManager.currentDrumset;
    if (this.projectManager.isDirty) {
      await this.projectManager.save();
    }
    await this.convertDrumset();
    if (replace) {
      this.deleteDrumset(drumset);
    }
  }

  @action
  async saveAndCopyDrumsetAndContinue() {
    await this.projectManager.save();

  }

  @action
  async addSamples() {
    // eslint-disable-next-line no-undef
    let handles: FileSystemFileHandle[];
    try {
      handles = await window.showOpenFilePicker({
        multiple: true,
        excludeAcceptAllOption: true,
        id: 'samples',
        startIn: 'music',
        types: [
          {
            description: 'Wave files',
            accept: {
              'audio/wav': ['.wav'],
            },
          },
        ],
      });
    } catch (err: any) {
      if (err.name === 'AbortError' && /user aborted/i.test(err.message)) {
        return;
      }
      throw err;
    }
    const files = await Promise.all(handles.map((handle) => handle.getFile()));
    this.#addSampleFiles(files);
  }

  async #addSampleFiles(files: FileList | File[], group?: VelocityGroup) {
    group = group ?? this.velocityGroups[0]!;
    const velocityValue = group.vel;

    let velocities: Velocity[] = [];
    for (let i = 0, ii = files.length; i < ii; i++) {
      let file = files[i]!;
      const buffer = await file.arrayBuffer();
      const velocity = await Drumset.parseWaveFile(buffer);
      velocity.vel = velocityValue;
      velocity.fileName = bbffFilename(file.name);
      if (!this.instrument.velocities.find((v) => v.crc === velocity.crc)) {
        velocities.push(velocity);
      }
    }
    if (velocities.length) {
      // If this is the first velocity group and it has no samples.
      if (
        this.velocityGroups.length === 1 &&
        group.velocities.length === 1 &&
        !group.velocities[0]!.data
      ) {
        velocities = autoSort(velocities).map(([sample, velocity]) => {
          sample.vel = velocity;
          return sample;
        });
      }
      // A newly added instrument will have a length of 1, but no data.
      // this is because of the line this.instrument.addVelocities([new Velocity(0)]);
      // in AddNewInstrumentToDrumsetCommand. That exists for visual purposes (try it without that - no 0 at the bottom of the velocity bar)
      // We only want to check real samples, so we filter out those with no data.
      let currentVelocities = this.instrument.velocities.filter(v => v.data !== undefined).length
      if (
        !this.projectManager.isCurrentDrumsetDRMX() &&
        (this.instrument.velocities.length > 16 ||
          (currentVelocities + velocities.length > 16))
      ) {
        if (!this.projectManager.isProjectDRMX) {
          alert(
            'You can only have a maximum of 16 samples per instrument with a BeatBuddy 1 drumset. Please convert your project to a BeatBuddy 2 project to add more samples.',
          );
        } else {
          const isDark = localStorage.getItem('theme') === 'dark';
          Swal.fire({
            title: 'Too Many Samples',
            html: 'You can only have a maximum of 16 samples per instrument in a BeatBuddy 1 drumset, but your drumset can be converted to a BeatBuddy 2 drumset. <br><br>Press "Save project and continue" to save your project and convert the drumset <br><br>"Save project, copy drumset, and continue" to save your project and convert a copy of the drumset<br><br>"Cancel" to cancel the operation.',
            icon: 'warning',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Save Project and Continue',
            denyButtonText: 'Save and Copy Drumset',
            cancelButtonText: 'Cancel',
            focusCancel: true,
            background: isDark ? '#1e1b2e' : undefined,
            color: isDark ? '#ffffff' : undefined,
          }).then((result) => {
            if (result.isConfirmed) {
              // 'Save Project and Continue' - replace drumset
              this.saveAndConvert(true);
            } else if (result.isDenied) {
              // 'Save and Copy Drumset' - copy drumset without replacement
              this.saveAndConvert(false);
            } else if (result.dismiss === Swal.DismissReason.cancel) {
              // 'Cancel' - do nothing
            }
          });
        }
        return;
      } else if (
        this.projectManager.isCurrentDrumsetDRMX() &&
        (this.instrument.velocities.length > 32 ||
          this.instrument.velocities.length + velocities.length > 32)
      ) {
        alert(
          'You can only have a maximum of 32 samples per instrument with a BeatBuddy 2 drumset.',
        );
        return;
      }
      const command = new AddSamplesToInstrumentCommand(this.instrument, velocities);
      this.undoManager.executeCommand(this.removeBlankSample(group, command));
    }
  }

  removeBlankSample(group: VelocityGroup, command: Command<any>) {
    const blankVelocity = group.velocities.find((v) => !v.sample);
    if (blankVelocity) {
      return new CompositeCommand(command.name, [
        command,
        new RemoveSampleFromInstrumentCommand(this.instrument, blankVelocity),
      ]);
    } else {
      return command;
    }
  }

  _cancelEvents(ev: DragEvent) {
    ev.stopPropagation();
    ev.preventDefault();
  }

  @action
  deleteSelectedSample(ev: KeyboardEvent) {
    if (this.triggeredVelocity) {
      ev?.stopImmediatePropagation();
      let command: Command<any> = new RemoveSampleFromInstrumentCommand(
        this.instrument,
        this.triggeredVelocity,
      );
      command.on('execute', () => {
        this.triggeredVelocity = undefined;
      });
      command.on('reverse', (velocity) => {
        this.triggeredVelocity = velocity;
      });

      if (this.instrument.velocities.length === 1) {
        command = new CompositeCommand(command.name, [
          command,
          new AddSamplesToInstrumentCommand(this.instrument, [new Velocity(0)]),
        ]);
      }
      this.undoManager.executeCommand(command);
    }
  }

  @action
  autoSort() {
    const commands = autoSort(this.instrument.velocities).map(([sample, velocity]) => {
      return new UpdateSampleVelocityCommand(this.instrument, sample, velocity);
    });
    this.undoManager.executeCommand(new CompositeCommand('Auto sort samples', commands));
  }

  get velocitiesByLevel() {
    return this.instrument.velocities.toSorted((v1, v2) => v1.loudness - v2.loudness);
  }

  @action
  updateVelocity(group: VelocityGroup, ev: Event) {
    let initialVelocity = group.vel;
    let velocity = parseInt((<HTMLInputElement>ev.target).value) + 1;
    if (isNaN(velocity)) {
      velocity = initialVelocity;
    } else if (velocity < 0) {
      velocity = 0;
    } else if (velocity > 127) {
      velocity = 127;
    }
    const sample = group.velocities[0]!;
    if (!sample.data) {
      sample.vel = velocity;
    } else {
      clearTimeout(this.undoUpdateTimer);
      group.vel = velocity;
      this.undoUpdateTimer = window.setTimeout(() => {
        this.performVelocityUpdate(group, velocity);
      }, 800);
    }
  }

  @action
  handleUpdateVelocity(evt: Event) {
    const ev = <CustomEvent>evt;
    const { group, velocity }: { group: VelocityGroup; velocity: number } = ev.detail;

    this.performVelocityUpdate(group, velocity);
  }

  performVelocityUpdate(group: VelocityGroup, velocity: number) {
    const commands = group.velocities.map(
      (v) => new UpdateSampleVelocityCommand(this.instrument, v, velocity),
    );
    this.undoManager.executeCommand(new CompositeCommand('Update velocities', commands));
  }

  @action
  renameVelocity(velocity: Velocity, instrument: Instrument, name: string) {
    if (name != "") {
      this.undoManager.executeCommand(new RenameVelocityCommand(velocity, instrument, name));
    }
  }

  @action
  async playVelocity(velocity: Velocity, group: VelocityGroup, ev?: MouseEvent) {
    ev?.stopPropagation();
    this.selectedVelocityGroup = group;
    this.triggeredVelocity = velocity;

    if (!velocity.audio && velocity.sample) {
      await this.audioContext.decodeAudioData(velocity.sample).then((audio) => {
        velocity.audio = audio;
      });
    }

    this.audioContext.resume();
    const source = this.audioContext.createBufferSource();
    // Through the master gain rather than straight out, so auditioning a sample
    // is heard at the kit's trimmed level and registers on the level meter.
    source.connect(this.songPlayer.drumsetVolume);
    source.buffer = velocity.audio!;
    source.start(0);
  }

  @action
  clearSelection() {
    this.triggeredVelocity = undefined;
  }

  @action
  calculateVelocityIndex(idx: number) {
    return Math.abs(idx - this.velocityGroups.length);
  }

  @action
  calculateVelocityRange(idx: number) {
    let velocityVal = this.velocityGroups.at(idx)!.vel;
    if (idx === 0) {
      return velocityVal + "-" + 127;
    } else {
      return velocityVal + "-" + (this.velocityGroups.at(idx - 1)!.vel - 1);
    }
  }

  @action
  velocityInRangeIsGreater(velocity: number, idx: number) {
    let velocityVal = this.velocityGroups.at(idx)!.vel;
    return velocityVal == velocity;
  }

  @tracked triggeredVelocity?: Velocity;
  @tracked selectedVelocityGroup?: VelocityGroup;
  playSamples = modifier((element: HTMLDivElement) => {
    let elementRect: DOMRect;
    let volume = 0;
    let intervalId: number = 0;
    let triggerTimeout = 1000;

    const playSample = () => {
      const velocityValue = constrainValue(Math.round(volume * 127), 0, 127);
      const [gainNode, velocity] = getGainNodeForVelocity(
        this.songPlayer.audioContext,
        this.songPlayer.drumsetVolume,
        this.instrument,
        velocityValue,
      );
      if (velocity && gainNode) {
        if (velocity.audio) {
          this.triggeredVelocity = velocity;
          // getGainNodeForVelocity has already connected this node to the master
          // gain; connecting it to the destination as well would sum both paths
          // and play the sample twice, half of it ignoring the kit volume.
          const source = this.audioContext.createBufferSource();
          source.connect(gainNode);
          source.buffer = velocity.audio;
          source.start(0);
          const off = this.audioContext.currentTime + triggerTimeout / 1000;
          if (this.instrument.nonPercussion) {
            gainNode.gain.setValueAtTime(volume, off - 0.05);
            gainNode.gain.exponentialRampToValueAtTime(0.05, off);
            source.stop(off);
          }
          return source;
        } else {
          console.error('No audio for instrument');
        }
      }
    };

    const setVolume = (ev: MouseEvent) => {
      volume = (elementRect.height - (ev.clientY - elementRect.top)) / elementRect.height;
    };

    const onMouseUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      clearInterval(intervalId);
      volume = 0;
      this.triggeredVelocity = undefined;
      ev.preventDefault();
      ev.stopPropagation();
    };

    const onMouseMove = (ev: MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      setVolume(ev);
    };

    const onMouseDown = (ev: MouseEvent) => {
      if (ev.eventPhase === Event.CAPTURING_PHASE) {
        return;
      }
      ev.preventDefault();
      ev.stopPropagation();
      this.audioContext.resume();

      elementRect = element.getBoundingClientRect();
      setVolume(ev);
      clearInterval(intervalId);
      playSample();
      intervalId = setInterval(playSample, triggerTimeout);

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    element.addEventListener('mousedown', onMouseDown, { capture: true });

    return () => {
      element.removeEventListener('mousedown', onMouseDown);
    };
  });

  @action
  velocityStyle(velocity: number) {
    return htmlSafe(`bottom: ${(velocity / 127) * 100}%;`);
  }

  @action
  addSampleGroup(velocity: number) {
    const nextVel =
      this.velocityGroups.toReversed().find((g) => g.vel > velocity)?.vel ?? 127;
    const newVelocity = Math.floor((velocity + nextVel) / 2);
    const newSample = new Velocity(newVelocity);
    this.undoManager.executeCommand(
      new AddSamplesToInstrumentCommand(this.instrument, [newSample]),
    );
  }

  @action
  removeSampleGroup(group: VelocityGroup) {
    const commands: Command<any>[] = [];
    group.velocities.forEach((velocity) => {
      commands.push(new RemoveSampleFromInstrumentCommand(this.instrument, velocity));
    });
    this.undoManager.executeCommand(
      new CompositeCommand('Remove velocity group', commands),
    );
  }

  @action
  moveSample(
    group: VelocityGroup,
    fromElement: HTMLElement,
    fromIndex: number,
    toElement: HTMLElement,
    /*toIndex: number,*/
  ) {
    const sample = group.velocities[fromIndex];
    if (sample) {
      const targetVelocity = parseInt(toElement.dataset['velocityGroup']!);
      const toGroup = this.velocityGroups.find((g) => g.vel === targetVelocity)!;
      const command = new UpdateSampleVelocityCommand(
        this.instrument,
        sample,
        targetVelocity,
      );
      this.undoManager.executeCommand(this.removeBlankSample(toGroup, command));
    }
  }

  minVelocity(groups: VelocityGroup[], idx: number) {
    return idx === groups.length - 1 ? 0 : groups[idx + 1]!.vel + 1;
  }

  displayVelocity(group: VelocityGroup) {
    if (group.vel === 0) {
      return 0;
    }
    return group.vel - 1;
  }

  maxVelocity(groups: VelocityGroup[], idx: number) {
    return idx === 0 ? 126 : groups[idx - 1]!.vel - 1;
  }

  @action
  updateVelocityValue(group: VelocityGroup, evt: Event) {
    const ev = <CustomEvent>evt;
    if (ev.detail.group == group) {
      const velocity = ev.detail.velocity;
      (<HTMLInputElement>ev.currentTarget).value = velocity;
    }
  }

  <template>
    {{yield this.addSamples this.autoSort}}
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class="flex flex-col m-2 overflow-auto"
      ...attributes
      {{onKey "Backspace" this.deleteSelectedSample capture=true}}
      {{onKey "Delete" this.deleteSelectedSample capture=true}}
      {{on "click" this.clearSelection}}
    >
      <div class="flex h-full gap-2 mt-4 overflow-auto min-h-32">
        <div
          class="relative w-16 h-full shrink-0 velocity-bar bg-gradient-to-b from-red-800 to-red-300 cursor-vol dark:from-primary-darkGradientStart dark:to-primary-darkGradientEnd"
          {{this.playSamples}}
          {{on "bbff.group-vel-change" this.handleUpdateVelocity}}
        >
          {{#each this.velocityGroups as |group idx|}}
            {{#if (notEq group.vel 0)}}
              <div
                class="absolute left-0 w-full h-1 bg-white bg-opacity-80 cursor-ns-resize dark:bg-primary-darkComponent"
                style={{this.velocityStyle group.vel}}
                {{changeVelocity
                  group
                  (this.minVelocity this.velocityGroups idx)
                  (this.maxVelocity this.velocityGroups idx)
                }}
              />
            {{/if}}
          {{/each}}
        </div>
        <div class="w-full overflow-auto">
          <div class="flex flex-col w-full gap-2 overflow-auto h-max">
            <div class="relative flex min-w-full gap-4 overflow-y-auto">
              <div class="flex items-center">
                <input
                  type="number"
                  aria-label="Velocity"
                  autocomplete="off"
                  onwheel="this.blur()"
                  value="127"
                  class="w-16 p-2 font-bold text-center border disabled:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 vel-group dark:disabled:bg-primary-darkHeader dark:border-primary-darkBorderColor"
                  {{disabled when=true}}
                />
              </div>
            </div>
            {{#each this.velocityGroups as |group idx|}}
              <div
                {{scrollIntoView
                  when=(this.velocityInRangeIsGreater this.triggeredVelocity.vel idx)
                  opts=(hash block="nearest")
                }}
              >
                <div class="flex flex-col px-24 overflow-y-auto">
                  <h1 class="self-start text-xl font-bold text-primary dark:text-primary-darkButtonSelected border-b border-primary dark:border-primary-darkButtonSelected">
                    Dynamic Group {{this.calculateVelocityIndex idx}} ({{this.calculateVelocityRange idx}})
                  </h1>
                </div>
                <div class="relative flex min-w-full gap-4 overflow-y-auto items-end">
                  {{#unless (eq group.vel 126)}}
                    <div class="absolute top-16 -translate-y-1/2">
                      <Button
                        @icon="plus"
                        {{on "click" (fn this.addSampleGroup group.vel)}}
                      />
                      {{#if (gt this.velocityGroups.length 1)}}
                        <Button
                          @icon="xmark"
                          title={{group.vel}}
                          {{on "click" (fn this.removeSampleGroup group)}}
                        />
                      {{/if}}
                    </div>
                  {{/unless}}
                  <div class="flex items-center">
                    <input
                      type="number"
                      aria-label="Velocity"
                      min={{this.minVelocity this.velocityGroups idx}}
                      max={{this.maxVelocity this.velocityGroups idx}}
                      value={{this.displayVelocity group}}
                      autocomplete="off"
                      onwheel="this.blur()"
                      class="w-16 p-2 font-bold text-center border disabled:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 vel-group dark:bg-primary-darkComponent dark:disabled:bg-primary-darkHeader dark:border-primary-darkBorderColor"
                      {{disabled when=(eq group.vel 0)}}
                      {{on "change" (fn this.updateVelocity group)}}
                      {{on "bbff.group-vel-change" (fn this.updateVelocityValue group)}}
                    />
                  </div>
                  <div
                    class="flex w-full mb-10 h-32 gap-2 p-2 overflow-x-auto border-2 border-transparent border-dashed min-h-20 grow droppable:border-green-500"
                    data-velocity-group={{group.vel}}
                    {{markDroppable this.isDragging}}
                    {{on "dragenter" this.handleDragEnter}}
                    {{on "dragover" this.handleDragOver}}
                    {{on "dragleave" this.handleDragLeave}}
                    {{on "drop" (fn this.handleDrop group)}}
                    {{sortable
                      draggable=".sample"
                      group="samples"
                      sort=false
                      onMove=(fn this.moveSample group)
                      swapThreshold=0.65
                    }}
                  >
                    {{#each group.velocities as |velocity|}}
                      {{#if (notEq null velocity.data)}}
                      <div
                          role="button"
                          class="p-2 border rounded bg-primary-light selected:bg-primary sample dark:bg-primary-darkSample dark:selected:text-black dark:selected:bg-primary-darkButtonSelected dark:border-primary-darkBorderColor"
                          {{selected when=(eq this.triggeredVelocity velocity)}}
                          {{on "click" (fn this.playVelocity velocity group)}}
                        >
                          <div class="flex flex-col h-24">
                            <div class="flex-grow flex items-center justify-center">
                              <SelectableItem
                                @model={{velocity}}
                                @field="name"
                              {{!--  @filter={{nameFilter}} --}}
                                class="!bg-transparent !hover:bg-transparent !selected:bg-transparent"
                                @onChange={{fn this.renameVelocity velocity this.instrument}}
                                title={{velocity.name}}
                              >
                                <div class="flex items-center justify-center w-auto h-auto max-w-24 max-h-16 whitespace-normal">
                                  {{velocity.name}}
                                </div>
                              </SelectableItem>
                            </div>
                            <div class="flex items-center justify-between gap-4 text-sm">
                              <div>{{round velocity.duration 2}} sec</div>
                              <div>{{round velocity.loudness 2}} dB</div>
                            </div>
                          </div>
                        </div>
                      {{/if}}
                    {{/each}}
                  </div>
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      </div>
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    SampleEditor: typeof SampleEditor;
  }
}
